import React, { useEffect, useState, useContext } from 'react'
import { Button, ButtonDiv, ConformityStatus, Content, LeftSideContainer, RightSide, StructureContainer, StructureHeader, StructureHeaderActions, Title } from './style'
import StructuringCertificateFields from './StructuringCertificateFields'
import axios from '../../../../config/axios'
import StructureColumnsInCertificate from './StructureColumns'
import saveImage from '../../../../assets/certificates/AddParameter/save.svg'
import { ParamType } from '../CreateParam'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { CertificateOutletType } from '..'
import view from '../../../../assets/global-assets/view.svg'
import EditStructureModal, { ColumnType } from '../../../../components/Modals/EditStructure'
import { Check, EllipsisVertical, Info } from 'lucide-react'
import SupplierFile from '../../../../components/Certificates/SupplierFile'
import ImagesInTheCertificate from './ImagesInTheCertificate'
import HeaderFields from './HeaderFields'
import { mergeCertificateWithServiceDetail } from '../../../../components/Certificates/SelectItem/opFromService'
import CertificateStructureOptions from './CertificateStructureOptions'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'

interface ColumnsInStructureWithValues {
  id: number
  concatenatedId: number
  value: string
  name?: string
  isNew?: boolean
  unitUsed?: {id:number, name:string}
  possibleUnits?: {id:number, name:string}[]
}

interface ImageData {
  id?: number
  file?: File
  fileUrl?: string
  fileSize?: number
  customName: string
  isEditing: boolean
  subtitle?: string
}

export interface CertificateType {
  param: {id:number, name:string, CertificateType?:{id:number,name:string}}
  columnsInStructureWithValues: ColumnsInStructureWithValues[]
  supplier: {id:number, companyName: string, contactName?: string, isMain?: boolean}
  customer: {id:number, companyName: string, contactName?: string}
  heatNumber: {id:number, heatNumber: string, originCertificateId?: number}
  date?: string
  description?: string
  startingMaterial?: string
  supplierFile?: File | string | null
  invoiceNumber?: string
  standard?: string
  heatTreatment?: string
  purchaseOrderNumber?: string
  observations?: string
  factory?: string
  codeOpString?: string
  serviceId?: number
}

export const verifyConformity = (column: ColumnType, certificate:CertificateType) => {
  const columnValue:number | null = Number(certificate.columnsInStructureWithValues.find(cv => cv.id === column.id)?.value.replace(',', '.'))
  const hasMin = column.min !== undefined && column.min !== null
  const hasMax = column.max !== undefined && column.max !== null

  if (!hasMin && !hasMax) return true
  
  if (hasMin && hasMax) return Number(columnValue) >= Number(column.min) && Number(columnValue) <= Number(column.max)
  
  if (hasMin) return columnValue >= Number(column.min)
  
  if (hasMax) return columnValue <= Number(column.max)
  
  return true
}

const CreateCertificate = () => {
  const [ searchParam ] = useSearchParams()
  const id = searchParam.get('id')
  const opIdParam = searchParam.get('opId') ?? searchParam.get('serviceId')
  const fromParam = searchParam.get('from')
  const attachmentIdParam = searchParam.get('attachmentId')
  const navigate = useNavigate()

  const handleNavigateBack = () => {
    const returnTo = searchParam.get('returnTo')
    if (returnTo) {
      navigate(returnTo)
      return
    }
    if (fromParam === 'attachments' && attachmentIdParam) {
      navigate(`/panel/attachments?attachmentId=${attachmentIdParam}`)
      return
    }
    if (fromParam === 'attachments') {
      navigate('/panel/attachments')
      return
    }
    navigate('..')
  }
  const { setLoading } = useOutletContext<CertificateOutletType>()
  const { setFeedback } = useContext(FeedbackContext)
  const [ images, setImages ] = useState<ImageData[]>([])
  const [ showRange, setShowRange ] = useState<number | null>(null)
  const [ structureMenuOpenId, setStructureMenuOpenId ] = useState<number | null>(null)
  const [ editStructureModalOpen, setEditStructureModalOpen ] = useState(false)
  const [ clickedStructureId, setClickedStructureId ] = useState(0)
  const [ certificate, setCertificate ] = useState<CertificateType>({
    param: {id:0, name:''},
    customer: {id: 0, companyName: ''},
    supplier: {id: 0, companyName: ''},
    heatNumber: {id: 0, heatNumber: ''},
    columnsInStructureWithValues: [],
    codeOpString: ''
  })
  const bodyToSend = {
    paramId: certificate.param.id,
    customerCompanyId: certificate.customer.id,
    supplierCompanyId: certificate.supplier.id,
    heatNumberId: certificate.heatNumber.id,
    columnsInStructureWithValues: certificate.columnsInStructureWithValues,
    description: certificate.description,
    startingMaterial: certificate.startingMaterial,
    invoiceNumber: certificate.invoiceNumber,
    standard: certificate.standard,
    heatTreatment: certificate.heatTreatment,
    purchaseOrderNumber: certificate.purchaseOrderNumber,
    observations: certificate.observations,
    factory: certificate.factory,
    supplierFile: !(certificate.supplierFile instanceof File) ? certificate.supplierFile : undefined,
    codeOpString: certificate.codeOpString,
    serviceId:
      certificate.serviceId != null && certificate.serviceId > 0
        ? certificate.serviceId
        : 0,
  }
  const [ param, setParam ] = useState<ParamType>({
    id: 0,
    name: '',
    structures: []
  })

  useEffect(() => {
    if(id) {
      setLoading(true)
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${id}`)
        .then(response => {
          if(response.data) {
            const certificateData = response.data
            const { images, ...rest } = certificateData
            setCertificate(rest)
            
            if (images && images.length > 0) {
              const existingImages: ImageData[] = images.map((img: any) => ({
                id: img.id,
                fileUrl: img.fileUrl,
                fileSize: img.fileSize,
                customName: img.title,
                subtitle: img.subTitle,
                isEditing: false
              }))
              setImages(existingImages)
            }
          }
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [id])

  useEffect(() => {
    if (id || !opIdParam) return
    const opId = parseInt(opIdParam, 10)
    if (Number.isNaN(opId) || opId <= 0) return
    axios
      .get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}`,
      )
      .then((res) => {
        if (res.data) {
          setCertificate((prev) =>
            mergeCertificateWithServiceDetail(prev, res.data),
          )
        }
      })
      .catch(() => {})
  }, [id, opIdParam])

  useEffect(() => {
    if(certificate.param.id){
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/param/${certificate.param.id}`)
        .then(response => {
          if((response.data)) setParam(() => {
            const data:ParamType = response.data 
            return {
              id: data.id,
              name: data.name,
              structures: data.structures,
              type: data.type,
            }
          })
        })
        .catch(error => {
          console.error(error)
        })
    }
  }, [certificate.param.id])

  useEffect(() => {
    if(!id) {
      const columns: ColumnsInStructureWithValues[] = []
      param.structures.forEach(structure => {
        structure.columns.forEach((column: any) => {
          columns.push({
            id: column.id,
            value: '',
            concatenatedId: column.concatenatedId,
            name: column.name,
            possibleUnits: [],
            unitUsed: column.unitUsed
          })
        })
      })

      setCertificate(prev => ({
        ...prev,
        paramId: param.id || 0,
        columnsInStructureWithValues: columns
      }))
    }
  }, [param.id])
  
  useEffect(() => {
    if ((param.structures.length === 0 && certificate.columnsInStructureWithValues.length === 0) || location.pathname.includes('create')) return
    const newColumns: ColumnsInStructureWithValues[] = []
    
    param.structures.forEach(structure => {
      structure.columns.forEach((column: any) => {
        const existingColumn = certificate.columnsInStructureWithValues.find(cV => cV.id === column.id)
        
        if (!existingColumn) {
          newColumns.push({
            id: column.id,
            value: '',
            concatenatedId: column.concatenatedId,
            name: column.name,
            isNew: true
          })
        }
      })
    })
    
    if (newColumns.length > 0) {
      setCertificate(prev => ({
        ...prev,
        columnsInStructureWithValues: [...prev.columnsInStructureWithValues, ...newColumns]
      }))
    }
  }, [param.structures])

  const handleCreateEditCertificate = async () => {
    setLoading(true)

    try {
      let supplierFileUrl: string | null | undefined = undefined
      if (certificate.supplierFile === null) {
        supplierFileUrl = null
      } else if (certificate.supplierFile instanceof File) {
        const uploadedSupplier = await AttachmentApiService.upload(certificate.supplierFile, 'Certificado Fornecedor')
        supplierFileUrl = uploadedSupplier?.fileUrl || null
      } else if (typeof certificate.supplierFile === 'string' && certificate.supplierFile.trim()) {
        supplierFileUrl = certificate.supplierFile.trim()
      }

      const processedImages = await Promise.all(
        images.map(async (imageData) => {
          let generalFileId = imageData.id
          let fileUrl = imageData.fileUrl
          if (imageData.file) {
            const uploaded = await AttachmentApiService.upload(imageData.file, imageData.customName)
            generalFileId = uploaded?.id
            fileUrl = uploaded?.fileUrl
          }
          return {
            id: generalFileId,
            customName: imageData.customName,
            subtitle: imageData.subtitle || '',
            fileUrl,
          }
        }),
      )

      const fileIds = processedImages
        .map((img) => img.id)
        .filter((fileId): fileId is number => typeof fileId === 'number' && !isNaN(fileId))

      const bodyToSendWithImages = {
        ...bodyToSend,
        fileIds,
        imagesMetaData: processedImages,
        supplierFile: supplierFileUrl,
      }

      if (id) {
        await axios.put(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${id}`, bodyToSendWithImages)
        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Certificado atualizado com sucesso!',
        })
      } else {
        await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate`, bodyToSendWithImages)
        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Certificado criado com sucesso!',
        })
      }
      handleNavigateBack()
    } catch (error: any) {
      console.error(error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: error?.response?.data?.message || 'Erro ao salvar o certificado.',
      })
    } finally {
      setLoading(false)
    }
  }
    
  useEffect(() => {
    if (!certificate.heatNumber.originCertificateId || !certificate.heatNumber.id) return
    setLoading(true)

    const excludeParam = id ? `?excludeCertificateId=${id}` : ''
    const relatedUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/utils/related-attachments/${certificate.heatNumber.id}${excludeParam}`

    Promise.all([
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${certificate.heatNumber.originCertificateId}`),
      axios.get(relatedUrl),
    ])
      .then(([originResponse, relatedResponse]) => {
        if (originResponse.data) {
          setCertificate(prev => ({
            ...prev,
            supplierFile: originResponse.data.supplierFile,
            factory: originResponse.data.supplier.companyName,
            columnsInStructureWithValues: prev.columnsInStructureWithValues.map((column) => ({
              ...column,
              value: (originResponse.data.columnsInStructureWithValues.find((item: ColumnsInStructureWithValues) => item.id === column.id) || column).value
            }))
          }))
        }

        const anexos = relatedResponse.data ?? []
        if (Array.isArray(anexos) && anexos.length > 0) {
          setImages(prev => {
            const existentes = new Set(prev.map(i => i.fileUrl).filter(Boolean))
            const novos = anexos
              .filter((anexo: { fileUrl?: string }) => anexo.fileUrl && !existentes.has(anexo.fileUrl))
              .map((anexo: { fileUrl: string; customName: string; subtitle?: string | null }) => ({
                fileUrl: anexo.fileUrl,
                customName: anexo.customName,
                subtitle: anexo.subtitle ?? undefined,
                isEditing: false,
              }))
            return [...prev, ...novos]
          })
        }
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => setLoading(false))
  }, [certificate.heatNumber.id, certificate.heatNumber.originCertificateId, id])

  const syncCertificateColumnsFromParam = (nextParam: ParamType) => {
    setCertificate(prev => {
      const nextColumns: ColumnsInStructureWithValues[] = []
      nextParam.structures.forEach(structure => {
        structure.columns.forEach((column: any) => {
          const existing = prev.columnsInStructureWithValues.find(cV => cV.id === column.id)
          nextColumns.push({
            id: column.id,
            value: existing?.value ?? '',
            concatenatedId: column.concatenatedId,
            name: column.name,
            possibleUnits: existing?.possibleUnits ?? [],
            unitUsed: existing?.unitUsed ?? column.unitUsed,
            isNew: existing?.isNew,
          })
        })
      })
      return {
        ...prev,
        columnsInStructureWithValues: nextColumns,
      }
    })
  }

  const persistParamStructure = async (nextParam: ParamType) => {
    if (!nextParam.id) return
    setLoading(true)
    try {
      const { id: paramId, type, ...data } = nextParam
      await axios.put(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/param/${paramId}`,
        { ...data, certificateTypeId: type?.id },
      )
      const refreshed = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/param/${paramId}`,
      )
      if (refreshed.data) {
        const data: ParamType = refreshed.data
        const hydrated: ParamType = {
          id: data.id,
          name: data.name,
          structures: data.structures,
          type: data.type,
          class: data.class,
          family: data.family,
          astm: data.astm,
          uns: data.uns,
          din: data.din,
          aisi: data.aisi,
          others: data.others,
        }
        setParam(hydrated)
        syncCertificateColumnsFromParam(hydrated)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <Content>
      <LeftSideContainer>
        <StructuringCertificateFields certificate={certificate} setCertificate={setCertificate} />
        <SupplierFile certificate={certificate} setCertificate={setCertificate} />
      </LeftSideContainer>
      {
        certificate.param.id && certificate.customer.id && certificate.supplier.id ? (
          <RightSide>
            <HeaderFields certificate={certificate} setCertificate={setCertificate} />
            {param.structures.map((structure, index) => 
              <StructureContainer key={index}>
                <StructureHeader>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <Title>{structure.name}</Title>
                    {structure.columns.some(c => c.min || c.max) &&
                      <ConformityStatus outOfNorm={!structure.columns.every(c => verifyConformity(c, certificate))}>
                        {structure.columns.every(c => verifyConformity(c, certificate)) ? <><Check size={20} /> Valores conforme a norma</> : <><Info size={18} /> Algum valor está fora da norma </>}
                      </ConformityStatus>
                    }
                  </div>
                  <StructureHeaderActions>
                    {structure.columns.some(column => column.min !== null || column.max !== null) &&
                      <img
                        src={view}
                        alt='view-icon'
                        onMouseEnter={() => setShowRange(index)}
                        onMouseLeave={() => setShowRange(null)}
                      />
                    }
                    <EllipsisVertical
                      size={18}
                      style={{ cursor: 'pointer', color: '#696969' }}
                      onClick={() =>
                        setStructureMenuOpenId(prev =>
                          prev === structure.id ? null : structure.id,
                        )
                      }
                    />
                    {structureMenuOpenId === structure.id && (
                      <CertificateStructureOptions
                        onClose={() => setStructureMenuOpenId(null)}
                        onEdit={() => {
                          setClickedStructureId(structure.id)
                          setEditStructureModalOpen(true)
                        }}
                      />
                    )}
                  </StructureHeaderActions>
                </StructureHeader>
                <StructureColumnsInCertificate showRange={showRange} param={param} structureIndex={index} certificate={certificate} setCertificate={setCertificate} />
              </StructureContainer>
            )}
            <ImagesInTheCertificate images={images} setImages={setImages} />
          </RightSide>
        ) : null
      }
      {editStructureModalOpen && clickedStructureId > 0 && (
        <EditStructureModal
          onClose={() => setEditStructureModalOpen(false)}
          param={param}
          setParam={setParam}
          clickedStructure={clickedStructureId}
          onAfterSave={persistParamStructure}
        />
      )}
      <ButtonDiv>
        <Button onClick={handleNavigateBack}>Cancelar</Button>
        <Button onClick={handleCreateEditCertificate} isSave={true}> <img src={saveImage} /> Salvar certificado</Button>
      </ButtonDiv>
    </Content>
  )
}

export default CreateCertificate